import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, GraduationCap, Mail } from 'lucide-react';
import { PLATFORM_MODULES } from '@/content/platform';
import { INDUSTRIES } from '@/content/industries';
import { INK_U_URL, SALES_EMAIL, SUPPORT_EMAIL, orderLink, HELP_URL } from '@/lib/site';

const COLUMNS: Array<{ title: string; links: Array<{ label: string; to: string }> }> = [
  {
    title: 'Platform',
    links: [
      { label: 'Platform overview', to: '/platform' },
      ...PLATFORM_MODULES.map((m) => ({ label: m.name, to: `/platform/${m.slug}` })),
      { label: 'Hardware', to: '/hardware' },
    ],
  },
  {
    title: 'Cloud',
    links: [
      { label: 'Cloud services', to: '/cloud' },
      { label: 'Smart Assist AI', to: '/cloud#ai' },
      { label: 'Data Archive', to: '/cloud#archive' },
      { label: 'Remote Viewing', to: '/cloud#remote' },
      { label: 'SMS & Voice Alerts', to: '/cloud#alerts' },
      { label: 'Fleet Health', to: '/cloud#fleet' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Customer Portal', to: '/portal' },
    ],
  },
  {
    title: 'Industries',
    links: INDUSTRIES.map((i) => ({ label: i.name, to: `/industries#${i.slug}` })),
  },
  {
    title: 'Training & Partners',
    links: [
      { label: 'Ink U Training Portal', to: '/training' },
      { label: 'Certification Program', to: '/training/certification' },
      { label: 'Course Catalog', to: '/training#courses' },
      { label: 'Partner Program', to: '/partners' },
      { label: 'Apply to Partner', to: '/partners#apply' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/company' },
      { label: 'Resources', to: '/resources' },
      { label: 'Help Center', to: HELP_URL },
      { label: 'Contact', to: '/contact' },
      { label: 'License Builder', to: '/license-builder' },
      { label: 'Request a Quote', to: orderLink('quote') },
      { label: 'Place an Order', to: orderLink('order') },
      { label: 'Book a Demo', to: orderLink('demo') },
    ],
  },
];

export function SiteFooter() {
  return (
    <footer className="bg-navy-950 text-slate-400">
      {/* Pre-footer: the training portal, always one click away */}
      <div className="border-b border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-xl bg-brand-secondary/20 text-brand-accent flex items-center justify-center">
              <GraduationCap size={22} />
            </span>
            <div>
              <div className="text-white font-semibold">Learn Scada.Ink free on Ink U</div>
              <div className="text-sm">Video courses, quizzes and a two-level certification program.</div>
            </div>
          </div>
          <a
            href={INK_U_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[var(--radius-glass-sm)] bg-white text-navy-900 text-sm font-semibold hover:bg-white/90 transition-colors"
          >
            Start learning <ArrowUpRight size={15} />
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[1.3fr_repeat(5,1fr)]">
          <div>
            <Link to="/" className="inline-block rounded-xl bg-white p-2.5">
              <img src="/scada-ink-logo.png" alt="Scada.Ink" className="h-10 w-auto" />
            </Link>
            <p className="mt-5 text-sm leading-relaxed">
              Industrial monitoring, analytics and MES on a preconfigured appliance — your data stays on your network.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`mailto:${SALES_EMAIL}`} className="flex items-center gap-2 hover:text-white">
                <Mail size={14} /> {SALES_EMAIL}
              </a>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-2 hover:text-white">
                <Mail size={14} /> {SUPPORT_EMAIL}
              </a>
            </div>
            <Link to={orderLink('quote')} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5 transition-all">
              Sales inquiry <ArrowRight size={14} />
            </Link>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-white mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {/^https?:/.test(link.to) ? (
                      <a href={link.to} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.to} className="text-sm hover:text-white transition-colors">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <p>&copy; {new Date().getFullYear()} Scada.Ink. All rights reserved.</p>
          <p>Made with care for manufacturers everywhere.</p>
        </div>
      </div>
    </footer>
  );
}
